import { useCallback, useEffect, useState } from 'react';

export const OTP_COOLDOWN_SECONDS = 60;

const COOLDOWN_EVENT = 'otp-cooldown-change';

const storageKey = (phoneNumber: string): string =>
  `otpCooldownUntil:${phoneNumber}`;

export const secondsLeft = (until: number, now: number): number =>
  Math.max(0, Math.ceil((until - now) / 1000));

export const formatCooldown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
};

const readUntil = (phoneNumber: string): number => {
  if (!phoneNumber) {
    return 0;
  }
  const stored = Number(localStorage.getItem(storageKey(phoneNumber)));
  return Number.isFinite(stored) ? stored : 0;
};

interface OtpCooldown {
  remaining: number;
  coolingDown: boolean;
  start: () => void;
}

export const useOtpCooldown = (phoneNumber: string): OtpCooldown => {
  const [remaining, setRemaining] = useState(() =>
    secondsLeft(readUntil(phoneNumber), Date.now())
  );
  const [startedAt, setStartedAt] = useState(0);
  const coolingDown = remaining > 0;

  useEffect(() => {
    const restart = (): void => setStartedAt(Date.now());
    window.addEventListener(COOLDOWN_EVENT, restart);
    window.addEventListener('storage', restart);
    return () => {
      window.removeEventListener(COOLDOWN_EVENT, restart);
      window.removeEventListener('storage', restart);
    };
  }, []);

  useEffect(() => {
    const tick = (): number => {
      const next = secondsLeft(readUntil(phoneNumber), Date.now());
      setRemaining(next);
      return next;
    };
    if (tick() === 0) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (tick() === 0) {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phoneNumber, startedAt]);

  const start = useCallback((): void => {
    if (!phoneNumber) {
      return;
    }
    localStorage.setItem(
      storageKey(phoneNumber),
      String(Date.now() + OTP_COOLDOWN_SECONDS * 1000)
    );
    window.dispatchEvent(new Event(COOLDOWN_EVENT));
  }, [phoneNumber]);

  return { remaining, coolingDown, start };
};
